import events from './events.json'
import { v4 as uuidv4 } from 'uuid'
import { pick } from 'lodash-es'
import { setCookie } from '~/components/Cookie/controller/cookieController'

/**
 * Type guard preventing invalid event names usage.
 * Open `/packages/tracking/events.json` to check for all allowed names or add a new one..
 */
export type EventCode = keyof typeof events

/** Only primitive values are supported by ClickHouse */
type Primitive = string | number | boolean | null
export type Payload = { label?: string } & Record<string, Primitive | Primitive[]>

const AllowedQueryParams = [
  'cq_med',
  'cq_net',
  'cq_plac',
  'cq_plt',
  'cq_pos',
  'device',
  'fbclid',
  'ga_device',
  'ga_gclid',
  'ga_location',
  'ga_matchtype',
  'ga_utm_adgroup',
  'ga_utm_campaign',
  'ga_utm_content',
  'ga_utm_custom',
  'ga_utm_medium',
  'ga_utm_source',
  'ga_utm_term',
  'gbraid',
  'gclid',
  'location',
  'matchtype',
  'msclkid',
  'ttclid',
  'utm_adgroup',
  'utm_campaign',
  'utm_content',
  'utm_custom',
  'utm_location',
  'utm_matchtype',
  'utm_medium',
  'utm_source',
  'utm_term',
  'yclid',
  'utm_keyword',
  'lscid',
  'utm_lscid',
  'utm_subid',
  'utm_placement',
  'utm_region',
  'utm_id',
  'utm_device',
  'utm_gclid',
  'utm_msclkid',
]

/** Fetches the browser window and screen size */
const getResolutions = (): Payload => ({
  window_width: window.innerWidth,
  window_height: window.innerHeight,
  screen_width: window.screen.width,
  screen_height: window.screen.height,
  url: window.location.href,
})

/** Fetches the device type */
const getDevice = (): Payload => {
  const userAgent = window.navigator.userAgent
  const isMobile = /Mobi|Android/i.test(userAgent)
  const isTablet = /Tablet|iPad/i.test(userAgent)

  return {
    device: isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop',
  }
}

const getReferer = (): Payload => {
  const referer = document.referrer
  const priorReferer = referer || 'direct'

  return {
    prior_referer: priorReferer,
  }
}

const parseParams = (): Payload => {
  const params = new URLSearchParams(window.location.search)
  const payload = Object.fromEntries(params)

  return pick(payload, AllowedQueryParams)
}

const isNewUser = () => {
  if (typeof window !== 'undefined') {
    if (window.localStorage.getItem('is_new_user') === null) {
      window.localStorage.setItem('is_new_user', 'false')
      return true
    }
  }

  return false
}

const getCookie = (value: string): string | null => {
  const cookieValues = document.cookie
    .split('; ')
    .reduce((prev: Record<string, string>, current) => {
      if (!current) return prev

      const [name, ...rest] = current.split('=')
      prev[name] = rest.join('=')
      return prev
    }, {})

  return cookieValues[value]
}

const getVisitorId = (): string | null => {
  if (typeof window === 'undefined') return null

  let visitorId = getCookie('visitor_id')
  if (visitorId) return visitorId

  visitorId = uuidv4()
  setCookie('visitor_id', visitorId, window.location.hostname, 365)
  return visitorId
}

const Pages: {
  [x: string]: string
} = {
  '/': 'home',
  '/career-advice': 'career_advice_main',
  '/resume-builder': 'resume_builder_page',
  '/resume-builder-2': 'resume_builder_page_2',
  '/resume-examples': 'resume_examples',
  '/cover-letter-examples': 'cover_letter_examples',
  '/job-search': 'job_search',
  '/job-tracker': 'job_tracker',
  '/salary-analyzer': 'salary_analyzer',
  '/cv-builder': 'cv_builder',
  '/cover-letter-builder': 'cover_letter_builder',
  '/career-pathways': 'career_pathways',
  '/interview-prep': 'interview_prep',
  '/career-coach': 'career_coach',
  '/first-90-day-plan': 'first_90_day_plxan',
  '/pathway-to-promotion': 'pathway_to_promotion',
  '/virtual-hiring-events': 'virtual_hiring_events',
  '/professional-headshot': 'professional_headshot',
  '/career-exploration': 'career-exploration',
  '/job-search-strategy': 'job_search_strategy',
  '/resume-writing': 'resume_writing',
  '/executives': 'executives',
  '/partnership': 'partnership',
  '/total-job-search': 'total_job_search',
  '/recruiter-outreach': 'recruiter_outreach',
  '/career-assessment': 'career_assessment',
  '/resume-review': 'resume_review',
  '/student': 'student',
  '/early-careers': 'early_careers',
  '/mid-level-careers': 'mid_level_careers',
  '/career-changes': 'career_changes',
  '/about-us': 'about_us',
  '/press': 'press',
  '/enterprise': 'enterprise',
  '/work-for-us': 'work_for_us',
  '/contact': 'contact',
  '/tos': 'terms_of_use',
  '/privacy': 'privacy_policy',
  '/cookie-policy': 'cookie_policy',
  '/pricing': 'pricing',
  '/jobsearch': 'job_search',
}

export const getDefaultEventData = (): Payload => {
  const pathname = window.location.pathname
  const parsedPath = pathname.split('/').filter((n) => n)

  if (Object.keys(Pages).includes(pathname)) {
    const currentPath: keyof typeof Pages = pathname
    return {
      label: Pages[currentPath],
      website_host: 'career.io',
    }
  } else if (Object.keys(Pages).includes(pathname.substring(0, pathname.length - 1))) {
    const currentPath: keyof typeof Pages = pathname.substring(0, pathname.length - 1)
    return {
      label: Pages[currentPath],
      website_host: 'career.io',
    }
  } else if (Object.keys(Pages).includes('/' + parsedPath[0])) {
    const currentPath: keyof typeof Pages = '/' + parsedPath[0]
    return {
      label: Pages[currentPath],
      website_host: 'career.io',
    }
  }
  return {}
}

/**
 * Sends the event to the internal event tracking system (ClickHouse).
 */
export const trackEvent = (eventCode: EventCode, payload: Payload = {}) => {
  // timestamp in seconds since 1970 for attach to params
  const timestamp = Math.round(Date.now() / 1000)

  // attach screen and browser sizes to improve data analysis quality
  const params: Payload = {
    ...getDefaultEventData(),
    ...getResolutions(),
    ...getDevice(),
    ...getReferer(),
    ...parseParams(),
    ...payload,
    timestamp,
    visitor_id: getVisitorId(),
  }

  // call the endpoint via `sendBeacon` so it won't be aborted if a user leaves a page
  if ('navigator' in window && typeof navigator.sendBeacon === 'function') {
    try {
      navigator.sendBeacon(
        `/api/reporting/marketing-event/${eventCode}`,
        JSON.stringify({ params }),
      )
    } catch (error) {
      // TODO: move error logger to package and use it here
      console.error(error)
    }
  }

  // Improve DX by notifying about events tracking (including invalid ones)
  if (process.env.NODE_ENV === 'development') {
    if (!(eventCode in events)) {
      console.error(`🛑 ClickHouse Tracking: "${eventCode}" must be present in events.json`)
    } else {
      console.warn(`ClickHouse Tracking: Send "${eventCode}"`, params)
    }
  }
}

export const trackGlobalEvents = () => {
  const pathname = window.location.pathname
  // const searchParams = window.location.search
  const [parentPath, childPath] = pathname.split('/').filter((n) => n)

  if (
    Object.keys(Pages).includes(pathname) ||
    Object.keys(Pages).includes(pathname.substring(0, pathname.length - 1)) ||
    Object.keys(Pages).includes('/' + parentPath)
  ) {
    // const params = new URLSearchParams(searchParams.substring(1))
    if (isNewUser()) trackEvent('first_visit', { label: childPath || Pages[pathname] })
    trackEvent('website_visit', { label: childPath || Pages[pathname] })

    // const objParams = Object.fromEntries(params)
    // trackEvent('track_utm', objParams)
  }
}

/**
 * Send event to Google Analytics.
 */
export const trackMarketingEvent = (eventCategory: string, eventAction: string) => {
  // Create GTM event
  const gtmEvent = {
    event: 'gtmAppEvent',
    eventCategory,
    eventAction,
  }

  // pass the event to Google Tag Manager
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push(gtmEvent)
}
